// V244: Default Profile Photo Handler with PhotoStore (stable key cache)
// Purpose: Auto-save default Google image to Cloudinary on first load
// Uses PhotoStore stable key cache for instant display + mutex to prevent duplicate processes
// V278.0: Added upload guard to prevent placeholder images from being uploaded
// V280.0: Enhanced upload guard with validateUpload and size checks
// V281.0: Clean fix - added SVG detection, improved blob size validation
// V281.2: Final fix - detect Google letter placeholders (< 10KB), delete old unsigned services

import { fixedUnifiedPhotoService } from '../services/FixedUnifiedPhotoService';
import { getPhoto, setBlob, fetchAndCache } from './photoStore';
import { shouldUploadToCloudinary } from './cloudinaryUploadGuard';
import { validateUpload, UploadBlockedError } from './uploadGuard';

// Mutex: prevent multiple default photo processes running simultaneously
const defaultPhotoInFlight = new Map<string, Promise<string | null>>();

// In-memory flag to prevent duplicate uploads
const defaultPhotoUploaded = new Set<string>();

/**
 * Get default Google image URL from auth user metadata
 */
export function getDefaultGoogleImageUrl(user: any): string | null {
  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;
  if (typeof avatarUrl === 'string' && avatarUrl.length > 0) {
    // Get high-res version (2048x2048)
    if (avatarUrl.includes('googleusercontent.com')) {
      return avatarUrl.split('=s')[0] + '=s2048';
    }
    return avatarUrl;
  }
  return null;
}

/**
 * V244: Ensure default profile photo with PhotoStore (stable key cache)
 * - Checks PhotoStore first (instant if cached)
 * - If DB has Cloudinary URL, fetches and caches in PhotoStore
 * - If no photo, uses Google image and caches in PhotoStore immediately
 * - Uploads to Cloudinary in background (non-blocking, mutex-protected)
 * 
 * Mutex ensures only one default photo process runs per user at a time.
 */
export async function ensureDefaultProfilePhoto(
  userId: string,
  user: any,
  onPreviewReady?: (previewUrl: string) => void
): Promise<string | null> {
  // Mutex: if already in flight, return the existing promise
  const inFlight = defaultPhotoInFlight.get(userId);
  if (inFlight) {
    console.log('✅ V244: Default photo process already in flight (mutex guard)');
    return inFlight;
  }

  // Create the process promise
  const processPromise = (async (): Promise<string | null> => {
    try {
      // Step 1: Check PhotoStore first (instant if cached)
      const cachedPhoto = await getPhoto(userId);
      if (cachedPhoto && cachedPhoto.startsWith('blob:')) {
        console.log('✅ V244: Photo already cached in PhotoStore (instant)');
        if (onPreviewReady) {
          onPreviewReady(cachedPhoto);
        }
        return cachedPhoto;
      }

      // Step 2: Check DB for Cloudinary photo
      // V275.7: Use ONLY photo_urls[0] as single source of truth (removed profile_image_public_id)
      const { supabase } = await import('../integrations/supabase/client');
      const { data: userData } = await supabase
        .from('users')
        .select('photo_urls')
        .eq('id', userId)
        .maybeSingle();

      // V213.5: Guard - If user already has a real Cloudinary photo, do NOT overwrite
      const { hasRealCloudinaryPhoto } = await import('../utils/sanitizeUserUpdate');
      if (hasRealCloudinaryPhoto(userData)) {
        console.log('✅ V213.5: User already has Cloudinary photo - skipping default bootstrap (prevent overwrite)');
        
        // Still return the cached photo if available for UI display
        const photoUrls = userData?.photo_urls;
        if (Array.isArray(photoUrls) && photoUrls.length > 0) {
          const cloudinaryUrl = photoUrls.find((url: string) => 
            typeof url === 'string' && url.includes('res.cloudinary.com')
          );

          if (cloudinaryUrl) {
            const objectUrl = await fetchAndCache(userId, cloudinaryUrl);
            if (objectUrl && onPreviewReady) {
              onPreviewReady(objectUrl);
            }
            return objectUrl;
          }
        }
        
        return null;
      }

      const photoUrls = userData?.photo_urls;
      if (Array.isArray(photoUrls) && photoUrls.length > 0) {
        const cloudinaryUrl = photoUrls.find((url: string) => 
          typeof url === 'string' && url.includes('res.cloudinary.com')
        );

        if (cloudinaryUrl) {
          console.log('✅ V244: Found Cloudinary URL in DB, fetching and caching');
          // Fetch and cache in PhotoStore (stable key)
          const objectUrl = await fetchAndCache(userId, cloudinaryUrl);
          if (objectUrl && onPreviewReady) {
            onPreviewReady(objectUrl);
          }
          return objectUrl;
        }
      }

      // Step 3: Use Google default image (instant)
      const defaultImageUrl = getDefaultGoogleImageUrl(user);
      if (!defaultImageUrl) {
        console.log('⚠️ V244: No default Google image found');
        return null;
      }

      console.log('🖼️ V244: Using default Google image, caching in PhotoStore');

      // Fetch Google image and cache in PhotoStore immediately (stable key)
      const objectUrl = await fetchAndCache(userId, defaultImageUrl);
      if (!objectUrl) {
        console.warn('⚠️ V244: Failed to cache default image');
        return null;
      }

      // Notify UI immediately
      if (onPreviewReady) {
        onPreviewReady(objectUrl);
      }

      // Step 4: Upload to Cloudinary in background (mutex-protected)
      // V281.2: ONLY upload if it's a real Google OAuth photo (not a placeholder)
      // Only upload once per user (guarded by defaultPhotoUploaded set)
      if (!defaultPhotoUploaded.has(userId)) {
        // V281.2: Validate before uploading - block placeholders!
        if (!shouldUploadToCloudinary(defaultImageUrl)) {
          console.log('🚫 V281.2: Skipping upload - not a real photo (placeholder detected)');
          console.log('💡 V281.2: Placeholder will be rendered client-side only (not stored in Cloudinary)');
          return objectUrl;  // Return the cached blob URL for display
        }

        defaultPhotoUploaded.add(userId);

        // Upload in background (don't await - non-blocking)
        (async () => {
          try {
            console.log('📤 V281.2: Uploading Google OAuth photo to Cloudinary (background)...');
            console.log('🔍 V281.2: Source URL:', defaultImageUrl.substring(0, 80));

            // Fetch the image as blob
            const response = await fetch(defaultImageUrl);
            const blob = await response.blob();

            // V281.2: CRITICAL - Never upload SVGs (placeholders)
            const contentType = response.headers.get('content-type') || blob.type || '';
            if (contentType.includes('image/svg') || blob.type === 'image/svg+xml') {
              console.warn('🚫 V281.2: SVG content detected - skipping Cloudinary upload');
              console.log('💡 V281.2: Client-side ProfileHeroPhotoEmpty will render the placeholder');
              defaultPhotoUploaded.delete(userId);
              return;
            }

            // V281.2: Block Google's letter placeholders (small images < 10KB)
            // Google's real profile photos are typically > 50KB
            // Letter placeholders are usually 5-10KB
            if (blob.size < 10000) {
              console.warn('🚫 V281.2: Image too small (< 10KB), likely Google letter placeholder - skipping upload');
              console.log('💡 V281.2: Client-side ProfileHeroPhotoEmpty will render the placeholder');
              console.log('🔍 V281.2: Blob size:', blob.size, 'bytes');
              defaultPhotoUploaded.delete(userId);
              return;
            }

            const file = new File([blob], `default-${userId}.jpg`, { type: blob.type || 'image/jpeg' });

            // V281.2: NUCLEAR GUARD - Validate before upload
            try {
              validateUpload(file, userId, 'ensureDefaultProfilePhoto');
              console.log('✅ V281.2: Default photo validation passed');
            } catch (error) {
              if (error instanceof UploadBlockedError) {
                console.log('🚫 V281.2: Default photo upload blocked (expected for placeholders)');
                console.log('💡 V281.2: Will use client-side placeholder instead');
                defaultPhotoUploaded.delete(userId);
                return;
              }
              throw error;
            }

            // Get user name for Cloudinary display
            const userName = user?.user_metadata?.name ||
                             user?.user_metadata?.full_name ||
                             user?.email?.split('@')[0] ||
                             undefined;

            // Upload using the same pipeline as normal uploads
            const result = await fixedUnifiedPhotoService.uploadPhoto(
              userId,
              file,
              undefined, // No progress callback for background upload
              userName
            );

            if (result.success && result.photoUrl) {
              console.log('✅ V281.2: Google OAuth photo uploaded to Cloudinary:', result.photoUrl.substring(0, 80));
            } else {
              console.warn('⚠️ V281.2: Upload failed (non-critical):', result.error);
              // Remove from set so it can retry later
              defaultPhotoUploaded.delete(userId);
            }
          } catch (uploadError) {
            console.error('❌ V281.2: Upload exception (non-critical):', uploadError);
            // Remove from set so it can retry later
            defaultPhotoUploaded.delete(userId);
          }
        })();
      } else {
        console.log('✅ V281.2: Default photo upload already in progress (skip duplicate)');
      }

      return objectUrl;

    } catch (error) {
      console.error('❌ V244: ensureDefaultProfilePhoto failed:', error);
      return null;
    } finally {
      // Clear mutex when done
      defaultPhotoInFlight.delete(userId);
    }
  })();

  // Store promise in mutex
  defaultPhotoInFlight.set(userId, processPromise);
  
  return processPromise;
}

/**
 * Clear the in-memory flag (useful for testing or forced re-upload)
 */
export function clearDefaultPhotoFlag(userId: string): void {
  defaultPhotoUploaded.delete(userId);
}

